import React, { useEffect } from 'react';
import { LoadingSpinnerV2 } from 'shared-components/components';
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { DocumentError } from 'shared-components/images';

interface SubmittedPanelProps {
  url: string;
  embedded?: boolean;
  /** Called when the document load state changes (false = loading started, true = document loaded). */
  onLoadStateChange?: (loaded: boolean) => void;
}

const DocumentDisplayPanel = ({ url, embedded = false, onLoadStateChange }: SubmittedPanelProps): JSX.Element => {
  const [state, setState] = React.useState({ loading: true, error: false });
  const [reloadNonce, setReloadNonce] = React.useState(0);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setState({ loading: true, error: false });
    onLoadStateChange?.(false);
  }, [url, onLoadStateChange]);

  // Fixed page dimensions (approx A4 at 96 DPI: 210mm x 297mm -> ~794 x 1123 px)
  const PAGE_WIDTH = '790px';

  // reloadNonce used for triggering iframe refresh from retry button
  const effectiveUrl = reloadNonce === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}r=${reloadNonce}`;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        padding: '0px',
        borderRadius: embedded ? '0px' : '4px',
        borderWidth: embedded ? '1px 0px' : '1px',
      }}
      elevation={0}>
      <CardContent
        sx={{
          padding: '0px',
          paddingBottom: '0px !important',
          height: '100%',
        }}>
        <div
          style={{
            minWidth: PAGE_WIDTH,
            flex: '0 0 auto',
            height: '100%',
          }}>
          {state.error && (
            <Stack
              sx={{
                gap: 1,
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}>
              <DocumentError fontSize="small" />
              <Typography variant="body1" fontWeight={600}>
                Document not loading
              </Typography>
              <Stack sx={{ alignItems: 'center' }}>
                <Typography variant="body1" align="center">
                  The document is taking longer than expected to load.
                </Typography>
                <Typography variant="body1" align="center">
                  Please retry or contact support if the issue persists.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                data-testid="retry-button"
                onClick={(): void => {
                  setState({ loading: true, error: false });
                  onLoadStateChange?.(false);
                  setReloadNonce((prev) => prev + 1);
                }}>
                Retry
              </Button>
            </Stack>
          )}
          {state.loading && (
            <LoadingSpinnerV2
              loadingText={'Loading Document Viewer'}
              subtitleSequence={[
                'Generating your documents',
                'Your document is nearly ready, thanks for waiting',
                'This is taking longer than expected, still generating your document',
              ]}
              onTimeout={() => setState({ loading: false, error: true })}
            />
          )}
          <iframe
            title="simpage"
            key={effectiveUrl}
            src={effectiveUrl}
            width="100%"
            height="100%"
            ref={iframeRef}
            onLoad={(): void => {
              setState({ loading: false, error: false });
              onLoadStateChange?.(true);
              // Inject styles after iframe content is loaded
              const iframeDocument = iframeRef.current?.contentDocument;
              if (iframeDocument) {
                const style = iframeDocument.createElement('style');
                style.appendChild(
                  iframeDocument.createTextNode(
                    '.prescription, .simulation { padding: 8px; max-width: 900px; justify-self: center; border: 1px solid rgba(0, 0, 0, 0.12); margin: 0px auto; } .page-break { border: 1px solid rgba(0, 0, 0, 0.12); margin: 0px -8px; height: 14px; background-color: lightgray; border-left: none; border-right: none; }',
                  ),
                );
                iframeDocument.head.appendChild(style);
              }
            }}
            onError={(): void => {
              setState({ error: true, loading: false });
            }}>
            This browser does not support embedded documents.
          </iframe>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentDisplayPanel;
